import { storeDate, type IsoDate, type SalesSummary } from "@estelle/domain";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { ForbiddenError } from "@/adapters/api/base";
import { useDataSource } from "@/adapters/data-source";
import {
  EmptyState,
  LoadFailed,
  NoAccess,
} from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { ChartCard, FigureTable } from "@/components/charts/ChartCard";
import {
  DonutChart,
  RankedBarChart,
  TimeSeriesChart,
} from "@/components/charts/lazy";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { orderCountNote } from "@/modules/dashboard/revenue";
import { formatMoney } from "@/modules/gst/format";
import { DateRangeControls } from "@/modules/reports/DateRangeControls";
import { ReportHeader } from "@/modules/reports/ReportHeader";
import { rangeLabel } from "@/modules/reports/orderChart";
import {
  BUCKET_AXIS_LABELS,
  bucketLabels,
  effectiveSalesRange,
  encodeSalesRange,
  paymentStatusLabel,
  readSalesRange,
  salesPresets,
} from "@/modules/reports/sales";
import { formatCompactMoney, formatRupees } from "@/lib/chart-format";
import { cn } from "@/lib/utils";
import { reportingNow } from "@/lib/reportClock";
import { useSectionSearch, type SectionSearch } from "@/routes/search";

/**
 * Revenue over time, by shipping state and by payment status (FR-DSH-2,
 * tickets #96 and #486), for a range of the store's days.
 *
 * All three views are one answer — `orders.salesSummary` — counted where the
 * orders are, in the store's timezone: no order is downloaded to be added up
 * here, so the range can be a year as easily as a fortnight. One answer rather
 * than three reads, so the chart, both tables and the total beside them cannot
 * each apply a slightly different window and silently disagree about which
 * orders count (#161).
 *
 * The range is in the URL beside any other report setting, so a view can be
 * linked to.
 */
export function Sales() {
  const data = useDataSource();
  const navigate = useNavigate();
  const urlSearch = useSectionSearch();

  const today = storeDate(reportingNow());
  const read = readSalesRange(urlSearch.chart);
  const range = effectiveSalesRange(read.range, today);
  const presets = useMemo(() => salesPresets(today), [today]);

  /*
    A link whose dates the report cannot use opens on the default range, and
    says why. The address is then rewritten to the range on screen, so what the
    bar says and what the page shows are one thing — a link copied from here is
    the report that was looked at, not the one that was refused. The reason is
    kept until the next change of dates.
  */
  const [notice, setNotice] = useState<string | null>(null);
  const { problem, includeCancelled } = read;
  // The reason is picked up as the problem appears, while rendering rather than
  // in the effect below: the effect is only for the address bar, which is
  // outside React.
  const [reported, setReported] = useState<string | null>(null);
  if (problem !== reported) {
    setReported(problem);
    if (problem !== null) {
      setNotice(`${problem} The last 60 days are shown instead.`);
    }
  }
  useEffect(() => {
    if (problem === null) return;
    void navigate({
      to: ".",
      replace: true,
      search: (prev: SectionSearch) => ({
        ...prev,
        chart: encodeSalesRange(null, includeCancelled),
      }),
    });
  }, [problem, includeCancelled, navigate]);

  function setRange(next: { from: IsoDate; to: IsoDate }) {
    setNotice(null);
    void navigate({
      to: ".",
      search: (prev: SectionSearch) => ({
        ...prev,
        chart: encodeSalesRange(next, includeCancelled),
      }),
    });
  }

  // Beside the dates in the same `chart` parameter, as Reports > Orders keeps it.
  function setIncludeCancelled(next: boolean) {
    void navigate({
      to: ".",
      search: (prev: SectionSearch) => ({
        ...prev,
        chart: encodeSalesRange(read.range, next),
      }),
    });
  }

  const request = { ...range, includeCancelled };
  const query = useQuery({
    queryKey: ["reports", "sales", range.from, range.to, includeCancelled],
    queryFn: () => data.orders.salesSummary(request),
    // Keep the last report on screen, dimmed, while the next is counted: a
    // skeleton on every change of dates makes the page jump.
    placeholderData: keepPreviousData,
  });

  const controls = (
    <ReportHeader
      readAt={query.data ? query.dataUpdatedAt : undefined}
      controls={
        <section
          aria-label="Report dates"
          className="flex flex-col gap-2 lg:items-end"
        >
          <DateRangeControls
            idPrefix="sales"
            range={range}
            today={today}
            presets={presets}
            onChange={setRange}
            notice={notice}
            align="end"
          />
          <div className="flex items-center gap-2">
            <Checkbox
              id="sales-include-cancelled"
              checked={includeCancelled}
              onCheckedChange={(checked) => {
                setIncludeCancelled(checked === true);
              }}
            />
            <Label
              htmlFor="sales-include-cancelled"
              className="text-sm font-normal"
            >
              Include cancelled orders
            </Label>
          </div>
        </section>
      }
      footnote={
        <span data-testid="sales-basis">
          Days run midnight to midnight, India time.{" "}
          {includeCancelled
            ? "Cancelled orders are included."
            : "Cancelled orders are left out; turn on “Include cancelled orders” to count them."}
        </span>
      }
    />
  );

  // The section is hidden from someone without the permission, but a typed URL,
  // or a role changed since the page loaded, still lands here: say so, in the
  // shared words, instead of printing a failed request.
  if (query.error instanceof ForbiddenError) {
    return (
      <NoAccess permission={query.error.permission ?? "reports.sales.view"} />
    );
  }

  if (query.isError) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <LoadFailed
          message={query.error.message}
          onRetry={() => {
            void query.refetch();
          }}
        />
      </div>
    );
  }

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-6">
        {controls}
        <Card>
          <CardContent>
            <Skeleton className="h-48 w-full" />
          </CardContent>
        </Card>
        <TableSkeleton />
      </div>
    );
  }

  const summary = query.data;

  return (
    <div className="flex flex-col gap-6">
      {controls}
      {summary.totals.orders === 0 ? (
        <EmptyState
          title="No orders in these dates"
          description={`No orders were placed between ${rangeLabel(summary.from, summary.to)}.${includeCancelled ? "" : " Cancelled orders are left out."}`}
        />
      ) : (
        <div
          aria-busy={query.isPlaceholderData}
          className={cn(
            "flex flex-col gap-6 transition-opacity",
            query.isPlaceholderData && "opacity-50",
          )}
        >
          <Report summary={summary} />
        </div>
      )}
    </div>
  );
}

function Report({ summary }: { summary: SalesSummary }) {
  const { bucket, totals } = summary;
  const rangeText = rangeLabel(summary.from, summary.to);
  const total = formatMoney(totals.revenue);

  const rupees = (value: number) => formatMoney(value.toFixed(2));
  const shownStates = summary.byState.slice(0, STATES_CHARTED);

  return (
    <>
      <ChartCard
        title="Revenue over time"
        description={
          <>
            {total} across {orderCountNote(totals.orders)}, {rangeText}, by{" "}
            {bucket}.
            {bucket === "week"
              ? " Weeks run Monday to Sunday; only days inside the dates count."
              : bucket === "month"
                ? " Only days inside the dates count in the first and last month."
                : ""}
          </>
        }
        table={
          <FigureTable
            rows={summary.buckets}
            rowKey={(entry) => entry.date}
            columns={[
              {
                header: BUCKET_AXIS_LABELS[bucket],
                cell: (entry) =>
                  bucketLabels(entry.date, bucket, summary).detail,
              },
              {
                header: "Orders",
                numeric: true,
                cell: (entry) => entry.orders,
              },
              {
                header: "Revenue",
                numeric: true,
                strong: true,
                cell: (entry) => formatMoney(entry.revenue),
              },
            ]}
          />
        }
      >
        <TimeSeriesChart
          points={summary.buckets.map((entry) => ({
            ...bucketLabels(entry.date, bucket, summary),
            value: Number(entry.revenue),
            note: orderCountNote(entry.orders),
          }))}
          valueLabel="Revenue"
          formatValue={rupees}
          formatTick={formatCompactMoney}
          ariaLabel={`Revenue per ${bucket}, ${rangeText}. Total ${total} across ${orderCountNote(totals.orders)}.`}
        />
      </ChartCard>

      <div className="grid gap-6 lg:grid-cols-2">
        <ChartCard
          title="Revenue by shipping state"
          description={
            <>
              Where orders were shipped to, {rangeText}, highest revenue first.
              {summary.byState.length > shownStates.length
                ? ` The chart shows the top ${String(shownStates.length)} of ${String(summary.byState.length)} states; the table has all of them.`
                : ""}
            </>
          }
          table={
            <FigureTable
              rows={summary.byState}
              rowKey={(row) => row.state}
              columns={[
                { header: "State", cell: (row) => row.state },
                { header: "Orders", numeric: true, cell: (row) => row.orders },
                {
                  header: "Revenue",
                  numeric: true,
                  strong: true,
                  cell: (row) => formatMoney(row.revenue),
                },
              ]}
            />
          }
        >
          <RankedBarChart
            rows={shownStates.map((row) => ({
              key: row.state,
              label: row.state,
              value: Number(row.revenue),
              note: orderCountNote(row.orders),
            }))}
            valueLabel="Revenue"
            formatValue={formatCompactMoney}
            formatExact={formatRupees}
            ariaLabel={`Revenue by shipping state, ${rangeText}: ${shownStates
              .map((row) => `${row.state} ${formatMoney(row.revenue)}`)
              .join(", ")}.`}
          />
        </ChartCard>

        <ChartCard
          title="Revenue by payment status"
          description={
            <>
              Each status's share of {total}, {rangeText}. Mostly pending is
              normal here — cash on delivery ships before it is paid for.
            </>
          }
          table={
            <FigureTable
              rows={summary.byPaymentStatus}
              rowKey={(row) => row.status}
              columns={[
                {
                  header: "Status",
                  cell: (row) => paymentStatusLabel(row.status),
                },
                { header: "Orders", numeric: true, cell: (row) => row.orders },
                {
                  header: "Revenue",
                  numeric: true,
                  strong: true,
                  cell: (row) => formatMoney(row.revenue),
                },
              ]}
            />
          }
        >
          <DonutChart
            slices={summary.byPaymentStatus.map((row) => ({
              key: row.status,
              label: paymentStatusLabel(row.status),
              value: Number(row.revenue),
              color: PAYMENT_COLOURS[row.status],
              note: orderCountNote(row.orders),
            }))}
            total={formatCompactMoney(Number(totals.revenue))}
            totalLabel="revenue"
            formatValue={formatRupees}
            ariaLabel={`Revenue by payment status, ${rangeText}: ${summary.byPaymentStatus
              .map(
                (row) =>
                  `${paymentStatusLabel(row.status)} ${formatMoney(row.revenue)}`,
              )
              .join(", ")}.`}
          />
        </ChartCard>
      </div>
    </>
  );
}

/** A ranking longer than this is a list, not a picture; the table has the rest. */
const STATES_CHARTED = 12;

/**
 * A payment status's colour in the ring. By meaning, as the list pills are:
 * paid is green, waiting is amber, money gone back is orange or red.
 */
const PAYMENT_COLOURS: Record<
  SalesSummary["byPaymentStatus"][number]["status"],
  string
> = {
  paid: "var(--series-3)",
  pending: "var(--series-4)",
  authorized: "var(--series-1)",
  partially_paid: "var(--series-7)",
  partially_refunded: "var(--series-2)",
  refunded: "var(--series-5)",
  voided: "var(--series-8)",
  unknown: "var(--muted-foreground)",
};
